import { Input, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { company } from '../../../../models/entities/company/company';
import { platformCompaniesSelectors } from '../../../../state/ducks/platformCompanies/selectors';
import { RootState } from '../../../../state/store/store';
import { getTenantLoginUrl } from '../../../../utils/tenantUrl';
import Grid from '../../../shared/Grid/Grid';
import { ColumnType } from '../../../shared/Grid/types/Column';
import Button from '../../../shared/SmallComponents/Button';
import Loader from '../../../shared/SmallComponents/Loader';
import {
    PlatformActions,
    PlatformErrorBanner,
    PlatformGridWrapper,
    PlatformHeaderRow,
    PlatformHeaderWrapper,
    PlatformPageSubtitle,
    PlatformPageTitle,
    PlatformPageWrapper
} from '../shared/platformPageStyles';

const emptyCompany = (): Partial<company> => ({
    name: '',
    subDomain: '',
    type: 'FORWARDER',
    phoneNumber: '',
    vatNumber: '',
    address: ''
});

const PlatformCompaniesPageConnected = ({
    companies,
    showLoader,
    gridFilter,
    lastError,
    fetchCompanies,
    setGridFilter,
    saveCompany
}: any) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<Partial<company> | null>(null);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const openCreate = () => {
        setEditing(emptyCompany());
        setModalOpen(true);
    };

    const openEdit = (row: company) => {
        setEditing({ ...row });
        setModalOpen(true);
    };

    const columns: Array<ColumnType<company & { id: string }>> = [
        { field: 'name', width: '220px', minWidth: '180px', sortable: true },
        {
            field: 'subDomain',
            width: '100%',
            minWidth: '160px',
            sortable: true,
            renderer: (sub: string) => (
                <a href={getTenantLoginUrl(sub)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                    {sub || '—'}
                </a>
            )
        },
        { field: 'type', width: '100%', minWidth: '120px', sortable: true },
        { field: 'phoneNumber', width: '100%', minWidth: '120px', sortable: false },
        {
            field: 'id',
            title: 'Actions',
            width: '90px',
            textAlign: 'center',
            sortable: false,
            bindedFields: ['name', 'subDomain', 'type', 'phoneNumber', 'vatNumber', 'address', 'parentCompany'],
            renderer: (id: string, binded?: Array<any>) => (
                <ActionBtn
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        openEdit({
                            id,
                            name: binded?.[0],
                            subDomain: binded?.[1],
                            type: binded?.[2],
                            phoneNumber: binded?.[3],
                            vatNumber: binded?.[4],
                            address: binded?.[5],
                            parentCompany: binded?.[6]
                        } as company);
                    }}
                >
                    Edit
                </ActionBtn>
            )
        }
    ];

    const filtered = companies.filter(
        (c: company) =>
            !gridFilter ||
            c.name?.toLowerCase().includes(gridFilter.toLowerCase()) ||
            c.subDomain?.toLowerCase().includes(gridFilter.toLowerCase())
    );

    return (
        <PlatformPageWrapper>
            <PlatformHeaderWrapper>
                <PlatformHeaderRow>
                    <div>
                        <PlatformPageTitle>Organizations</PlatformPageTitle>
                        <PlatformPageSubtitle>
                            Add customer companies, edit their details, and open a tenant link when you need to sign in on their subdomain.
                        </PlatformPageSubtitle>
                    </div>
                    <PlatformActions>
                        <Input
                            placeholder="Search by name or subdomain"
                            value={gridFilter}
                            onChange={(e) => setGridFilter(e.target.value)}
                            style={{ width: 260 }}
                        />
                        <Button buttonType="hollow" onClick={fetchCompanies}>
                            Refresh
                        </Button>
                        <Button buttonType="filled" onClick={openCreate}>
                            Add organization
                        </Button>
                    </PlatformActions>
                </PlatformHeaderRow>
            </PlatformHeaderWrapper>
            {lastError && <PlatformErrorBanner>{lastError}</PlatformErrorBanner>}
            <PlatformGridWrapper>
                {showLoader ? (
                    <Loader />
                ) : (
                    <Grid
                        columns={columns}
                        data={filtered}
                        localization={{
                            fields: { name: 'Name', subDomain: 'Subdomain', type: 'Type', phoneNumber: 'Phone', id: 'Actions' }
                        }}
                        emptyStateText="No organizations found"
                    />
                )}
            </PlatformGridWrapper>
            <Modal
                title={editing?.id ? 'Edit organization' : 'Create organization'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={async () => {
                    if (!editing?.name?.trim()) return;
                    await saveCompany({ id: editing.id, data: editing });
                    setModalOpen(false);
                }}
                width={520}
            >
                <FormField label="Name">
                    <Input value={editing?.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </FormField>
                <FormField label="Subdomain (tenant URL)">
                    <Input value={editing?.subDomain} onChange={(e) => setEditing({ ...editing, subDomain: e.target.value.toLowerCase() })} />
                </FormField>
                <FormField label="Type">
                    <Input value={editing?.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} placeholder="FORWARDER, CARGO_OWNER, ..." />
                </FormField>
                <FormField label="Phone">
                    <Input value={editing?.phoneNumber} onChange={(e) => setEditing({ ...editing, phoneNumber: e.target.value })} />
                </FormField>
                <FormField label="VAT / registration number">
                    <Input value={editing?.vatNumber} onChange={(e) => setEditing({ ...editing, vatNumber: e.target.value })} />
                </FormField>
                <FormField label="Address">
                    <Input value={editing?.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
                </FormField>
            </Modal>
        </PlatformPageWrapper>
    );
};

const FormField = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <FieldWrap>
        <FieldLabel>{label}</FieldLabel>
        {children}
    </FieldWrap>
);
const FieldWrap = styled.div`margin-bottom: 12px;`;
const FieldLabel = styled.label`display: block; margin-bottom: 4px; font-weight: 600;`;
const ActionBtn = styled.button`
    border: none;
    background: none;
    color: ${(p) => p.theme.colors.link};
    cursor: pointer;
    font-weight: 600;
`;

const mapState = (state: RootState) => ({
    companies: platformCompaniesSelectors.filteredCompanies(state),
    gridFilter: platformCompaniesSelectors.gridFilter(state),
    lastError: platformCompaniesSelectors.lastError(state),
    showLoader: !!state.loading.effects.platformCompanies?.fetchCompanies
});

const mapDispatch = (dispatch: any) => ({
    fetchCompanies: () => dispatch.platformCompanies.fetchCompanies(),
    setGridFilter: (v: string) => dispatch.platformCompanies.setGridFilter(v),
    saveCompany: (p: { id?: string; data: Partial<company> }) => dispatch.platformCompanies.saveCompany(p)
});

export default connect(mapState, mapDispatch)(PlatformCompaniesPageConnected);
